import {
  Component, ElementRef, EventEmitter, forwardRef, Input, Output, signal, ViewChild,
  ContentChild, ChangeDetectionStrategy, booleanAttribute, inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AdminuiSelectOptionDirective } from '../adminui-select/adminui-select-option.directive';
import { AdminuiAutocompleteGroupDirective } from './adminui-autocomplete-group.directive';
import { nextAdminuiId } from '../adminui-unique-id';
import { AdminuiTruncationTitleDirective } from '../adminui-truncation-title.directive';
import { I18NService } from '../../../core/i18n/i18n.service';

/**
 * Emitted by `(completeMethod)` when the user types or opens the dropdown. Shape-compatible with
 * PrimeNG's `AutoCompleteCompleteEvent` (same name) so the app's handlers migrate unchanged, while
 * the type now lives with the in-house component instead of `primeng/autocomplete`.
 */
export interface AutoCompleteCompleteEvent { query: string; originalEvent: Event; }
/** Emitted by `(onSelect)`. Shape-compatible with PrimeNG's `AutoCompleteSelectEvent` (same name). */
export interface AutoCompleteSelectEvent { originalEvent: Event; value: any; }

/**
 * In-house autocomplete (type-in-field) styled with gaia-styles + built on CDK Overlay.
 *
 * API mirrors PrimeNG `p-autoComplete` so migration is a tag swap — the app's server-side search
 * handlers keep working unchanged:
 * - `[suggestions]` — results the parent sets (we do NOT filter; server-side).
 * - `(completeMethod)` emits `{ query, originalEvent }` (typing, or dropdown click where
 *   `originalEvent.type === 'click'`).
 * - `(onSelect)` emits `{ value }`; `(onBlur)` / `(onFocus)` mirror PrimeNG.
 * - `[dropdown]` toggle button, `[minLength]`, `optionLabel`, `[group]` + group template,
 *   custom item `<ng-template adminuiSelectOption>`.
 *
 * The selected value's label always shows in the input, independent of the current `suggestions`
 * (server-side lists churn). Selecting keeps focus (option mousedown is prevented) so blur does
 * not fire on pick.
 */
@Component({
  selector: 'adminui-autocomplete',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, OverlayModule, AdminuiTruncationTitleDirective],
  templateUrl: './adminui-autocomplete.component.html',
  styleUrl: './adminui-autocomplete.component.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AdminuiAutocompleteComponent), multi: true }
  ],
  host: {
    'class': 'adminui-autocomplete',
    '[class.adminui-autocomplete--open]': 'open()',
    '[class.adminui-autocomplete--disabled]': 'disabled()',
    '[class.adminui-autocomplete--invalid]': 'invalid'
  }
})
export class AdminuiAutocompleteComponent implements ControlValueAccessor {
  @Input() suggestions: any[] = [];
  @Input() optionLabel = '';
  @Input() placeholder = '';
  @Input() minLength = 1;
  @Input() emptyMessage = '';
  @Input({ transform: booleanAttribute }) dropdown = false;
  @Input({ transform: booleanAttribute }) group = false;
  @Input({ transform: booleanAttribute }) invalid = false;
  /** Accessible name for the combobox input + listbox (use when there is no associated visible label). */
  @Input('aria-label') ariaLabel = '';
  /** Id(s) of visible element(s) that label this control (WAI-ARIA). */
  @Input('aria-labelledby') ariaLabelledBy = '';

  // Event shapes mirror PrimeNG's AutoCompleteCompleteEvent / AutoCompleteSelectEvent so the
  // app's existing handlers (typed against those) migrate with no changes.
  @Output() completeMethod = new EventEmitter<AutoCompleteCompleteEvent>();
  @Output() onSelect = new EventEmitter<AutoCompleteSelectEvent>();
  @Output() onBlur = new EventEmitter<Event>();
  @Output() onFocus = new EventEmitter<Event>();

  @ViewChild('input', { static: true }) inputRef!: ElementRef<HTMLInputElement>;
  @ContentChild(AdminuiSelectOptionDirective) itemTpl?: AdminuiSelectOptionDirective;
  @ContentChild(AdminuiAutocompleteGroupDirective) groupTpl?: AdminuiAutocompleteGroupDirective;

  readonly open = signal(false);
  readonly disabled = signal(false);
  readonly value = signal<any>(null);
  readonly inputText = signal('');
  readonly activeIndex = signal(-1);

  /** Stable ids wiring the WAI-ARIA combobox → listbox → active-option relationships. */
  readonly listboxId = nextAdminuiId('adminui-autocomplete-listbox');
  optionId(index: number): string { return `${this.listboxId}-option-${index}`; }
  /** Id of the active option, exposed via `aria-activedescendant` (null when closed / none). */
  activeDescendant(): string | null {
    return this.open() && this.activeIndex() >= 0 ? this.optionId(this.activeIndex()) : null;
  }

  // Localized accessible name for the dropdown toggle button (reuse I18NService; no duplicate data).
  private i18n = inject(I18NService);
  get toggleLabel(): string { return this.i18n.messages['common']?.['ariaLabelToggleDropdown'] ?? 'Toggle dropdown'; }

  private onChangeFn: (v: any) => void = () => { };
  private onTouchedFn: () => void = () => { };

  // ── ControlValueAccessor ─────────────────────────────────────────────
  writeValue(value: any): void {
    this.value.set(value ?? null);
    this.inputText.set(this.labelOf(value));
  }
  registerOnChange(fn: (v: any) => void): void { this.onChangeFn = fn; }
  registerOnTouched(fn: () => void): void { this.onTouchedFn = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled.set(isDisabled); }

  // ── public API ───────────────────────────────────────────────────────
  focus(): void { this.inputRef.nativeElement.focus(); }
  hide(): void { this.open.set(false); this.activeIndex.set(-1); }

  // ── rendering rows (items + optional group headers) ───────────────────
  get rows(): Array<{ group?: any; item?: any; index: number }> {
    const out: Array<{ group?: any; item?: any; index: number }> = [];
    let idx = 0;
    if (this.group) {
      for (const g of this.suggestions ?? []) {
        out.push({ group: g, index: -1 });
        for (const it of g?.items ?? []) out.push({ item: it, index: idx++ });
      }
    } else {
      for (const it of this.suggestions ?? []) out.push({ item: it, index: idx++ });
    }
    return out;
  }
  get flatItems(): any[] {
    return this.group ? (this.suggestions ?? []).flatMap(g => g?.items ?? []) : (this.suggestions ?? []);
  }
  /** Grouped rows for the `role=group` listbox structure; each item carries its flat (keyboard) index. */
  get displayGroups(): Array<{ group: any; labelId: string; items: Array<{ item: any; index: number }> }> {
    let idx = 0;
    return (this.suggestions ?? []).map((g, gi) => ({
      group: g,
      labelId: `${this.listboxId}-group-${gi}`,
      items: (g?.items ?? []).map((item: any) => ({ item, index: idx++ }))
    }));
  }

  labelOf(option: any): string {
    if (option === null || option === undefined) return '';
    if (this.optionLabel && typeof option === 'object') return option[this.optionLabel] ?? '';
    return String(option);
  }
  /** Marks the option matching the current model value (by label) as selected in the list. */
  isSelected(item: any): boolean {
    const v = this.value();
    return v !== null && v !== undefined && this.labelOf(item) === this.labelOf(v);
  }

  // ── input / dropdown ─────────────────────────────────────────────────
  onInput(event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.inputText.set(query);
    if (query.length >= this.minLength) {
      this.completeMethod.emit({ query, originalEvent: event });
      this.open.set(true);
    } else {
      this.hide();
    }
  }

  onDropdownClick(event: MouseEvent): void {
    if (this.disabled()) return;
    if (this.open()) { this.hide(); return; }
    this.completeMethod.emit({ query: this.inputText(), originalEvent: event });
    this.open.set(true);
    this.focus();
  }

  onInputFocus(event: FocusEvent): void { this.onFocus.emit(event); }

  onInputBlur(event: FocusEvent): void {
    this.hide();
    this.onBlur.emit(event);
    this.onTouchedFn();
  }

  selectItem(item: any, event?: Event): void {
    this.value.set(item);
    this.inputText.set(this.labelOf(item));
    this.onChangeFn(item);
    this.onSelect.emit({ originalEvent: event ?? new Event('select'), value: item });
    this.hide();
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); if (this.open()) this.moveActive(1); break;
      case 'ArrowUp': event.preventDefault(); if (this.open()) this.moveActive(-1); break;
      case 'Enter': {
        const items = this.flatItems;
        if (this.open() && this.activeIndex() >= 0 && items[this.activeIndex()]) {
          event.preventDefault();
          this.selectItem(items[this.activeIndex()], event);
        }
        break;
      }
      case 'Escape': if (this.open()) { event.preventDefault(); this.hide(); } break;
    }
  }

  private moveActive(delta: number): void {
    const n = this.flatItems.length;
    if (!n) return;
    this.activeIndex.set((this.activeIndex() + delta + n) % n);
  }
}
